import Module from '@/store/resource/common/module';
import Fields from '@/store/resource/fields';
import api from '@/factories/api';
import { prepareFields } from '@/filters/helpers';
import { FIELD_TYPE_MAP } from '@/views/settings/fields/fieldConstants';
import { searchInArray } from '@/store/storeHelpers';
import Vue from 'vue';

import startCase from 'lodash/startCase';
import isEmpty from 'lodash/isEmpty';

let module = new Module(new Fields());

module.add('mutations', {
  updateFieldInBucket(state, { model }) {
    const { bucket_id, name } = model;
    let fieldsBucket = state.all.find((i) => {
      if (bucket_id) {
        return i.id === bucket_id;
      }

      return i.type === 'default';
    });

    Vue.set(fieldsBucket.fields, name, model);
  },

  deleteFieldInBucket(state, { model }) {
    const { bucket_id, name } = model;
    let fieldsBucket = state.all.find((i) => {
      if (bucket_id) {
        return i.id === bucket_id;
      }

      return i.type === 'default';
    });

    Vue.delete(fieldsBucket.fields, name);
  },
});

module.add('actions', {
  async validateField(state, data) {
    try {
      return await api().post('fields/validate', data);
    } catch (err) {
      throw err;
    }
  },
});

module.add('getters', {
  getSingleField(state) {
    return (field) => {
      const item = state.all.find((i) => i.fields[field]);
      if (item) {
        return { ...item.fields[field], bucketId: item.id };
      }
      return null;
    };
  },

  fieldsGroupedBySection(state, getters) {
    return (section) => {
      return getters.defaultFields.filter((i) => {
        return i.section === section;
      });
    };
  },

  preparedFields(state) {
    return state.all.reduce((acc, group) => {
      acc.push({ header: group.title, group: group.type });
      acc.push(...prepareFields(group));
      return acc;
    }, []);
  },

  additionalFields(state, getters) {
    return getters.preparedFields.filter((i) => i.group === 'rep');
  },

  attributesFields: (state, getters) => {
    return getters.preparedFields.filter((i) => i.group !== 'rep');
  },

  defaultFields: (state) => {
    const bucket = state.all.find((i) => i.type === 'default');
    if (!bucket?.fields) return [];
    return Object.values(bucket?.fields).sort((a, b) => a.order - b.order);
  },

  eventFields: (state, getters) => {
    return getters.additionalFields.filter(({ is_event }) => is_event);
  },

  filteredDefaultFields: (state, getters) => {
    if (!getters.search) return getters.defaultFields;

    return searchInArray(
      getters.defaultFields,
      ['name', 'label'],
      getters.search,
      false
    );
  },

  getFieldSchema: (state, getters) => {
    return (field) => {
      if (typeof field === 'string') {
        field = getters.getSingleField(field);
      }

      return generateFieldSchema(field);
    };
  },

  getFieldComponentType: () => {
    return (field) => getFieldType(field);
  },

  keyFields: (state) => {
    let uniques = ['mobile', 'email', 'first_name', 'last_name'];

    return state.all.reduce((acc, bucket) => {
      for (let key in bucket.fields) {
        let f = bucket.fields[key];
        if (f.iskey || uniques.includes(f.name)) {
          acc.push({
            bucketId: bucket.id,
            name: f.name,
            label: f.label === 'name' ? f.name.replace('_', ' ') : f.label,
            value: undefined,
          });
        }
      }
      return acc;
    }, []);
  },
});

const generateFieldSchema = (field) => {
  if (!field.name) return;

  let schema = {
    label: getFieldLabel(field),
    title: getFieldLabel(field),
    field: field.name,
    type: getFieldType(field),
    validation: field.name === 'name' ? ['required'] : '',
    options: getFieldOptions(field.values),
    dateEditable: field.type === 'date',
    section: field.section,
  };

  // additional params for ContactListField schema
  if (schema.type === 'ContactList') {
    schema = {
      ...schema,
      bucketId: field.bucketId,
      name: field.name,
      originalType: field.type,
      filters: field.filters,
    };
  }

  // additional params for RelatedCustomObject schema
  if (schema.type === 'RelatedCustomObject') {
    const relatedCustomObjectParams = [
      'custom_object_id',
      'custom_object_field',
      'custom_object_field_map',
      'custom_record_id',
    ];

    for (let param of relatedCustomObjectParams) {
      schema[param] = field[param];
    }
  }

  return schema;
};

const getFieldLabel = (field) => {
  let label = field.label === 'name' ? field.name : field.label;
  return startCase((label || '').replace(/_/g, ' '));
};

const getFieldOptions = (values) => {
  if (!Array.isArray(values)) {
    if (isEmpty(values)) return;

    return Object.entries(values).map(([key, value]) => ({
      text: value,
      value: key,
    }));
  }

  if (!values.length) return [];

  return values.reduce(
    (acc, i) => {
      acc.push({ text: i, value: i });
      return acc;
    },
    [{ text: ' ', value: ' ' }]
  );
};

const getFieldType = (field) => {
  const { values, type, originalType } = field || {};
  if (values?.length) {
    return 'Select';
  }

  return FIELD_TYPE_MAP[originalType || type];
};

export default module.create();
